import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Data Rights (DSAR) — admin list of pending data-subject access / deletion
// requests (GDPR/CCPA). This is a GENERAL consumer-privacy queue (any user can
// file a DSAR at /data-rights), NOT a Large Projects feature — it is reachable
// regardless of the Large Projects flag.
//
// Consumes besthand-api `listPendingDsarRequests` (returns `dsarRequests`)
// through the `/lp-graphql` proxy via `lpApi`. Admin-gated (requireAdmin).
// ============================================================================

const Q_DSAR = `
  query {
    listPendingDsarRequests {
      status errorMessage
      dsarRequests {
        id userId type status description resultUrl
        requestedAt processedAt processedBy rejectionReason
      }
    }
  }
`;

// processDsarRequest(dsarRequestId!, notes)
const M_PROCESS = `
  mutation ($dsarRequestId: String!, $notes: String) {
    processDsarRequest(dsarRequestId: $dsarRequestId, notes: $notes) {
      status errorMessage
      dsarRequest { id status processedAt }
    }
  }
`;

// rejectDsarRequest(dsarRequestId!, reason!)
const M_REJECT = `
  mutation ($dsarRequestId: String!, $reason: String!) {
    rejectDsarRequest(dsarRequestId: $dsarRequestId, reason: $reason) {
      status errorMessage
      dsarRequest { id status rejectionReason }
    }
  }
`;

const S = {
  page: { maxWidth: 1080, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '16px 20px', marginBottom: 12, boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  name: { fontSize: 17, fontWeight: 800, margin: 0 },
  meta: { color: '#6b7280', fontSize: 13, marginTop: 4, lineHeight: 1.5 },
  link: { color: '#e8232b', fontWeight: 700, textDecoration: 'none', fontSize: 13 },
  badge: (kind) => {
    const map = {
      PENDING: { bg: '#fff3d6', fg: '#946200' },
      PROCESSING: { bg: '#eef2ff', fg: '#3730a3' },
      COMPLETED: { bg: '#e7f6ec', fg: '#137a37' },
      REJECTED: { bg: '#fde7e8', fg: '#c2151c' },
    };
    const c = map[kind] || { bg: '#eef2f4', fg: '#5a6168' };
    return { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 999, background: c.bg, color: c.fg, whiteSpace: 'nowrap' };
  },
  typeTag: { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 999, background: '#eef2f4', color: '#5a6168', marginLeft: 8 },
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  actions: { display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' },
  btn: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnRed: { background: '#fff', color: '#c2151c', border: '1px solid #f3c9cb', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};

const fmtDate = (s) => { if (!s) return ''; try { return new Date(s).toLocaleString(); } catch (e) { return s; } };

const isPending = (r) => r.status === 'PENDING' || r.status === 'PROCESSING';

function Dsar() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [msg, setMsg] = useState(null);

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_DSAR);
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.listPendingDsarRequests;
        if (env && env.status === 200) setRequests(env.dsarRequests || []);
        else if (env) setMsg({ error: env.errorMessage || `Error ${env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not load DSAR requests: ${e.message}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (key, query, variables, opName, okText) => {
    setBusyId(key);
    setMsg(null);
    try {
      const res = await lpApi(query, variables);
      if (res.errors) { setMsg({ error: firstError(res) }); setBusyId(''); return; }
      const env = res.data && res.data[opName];
      if (env && env.status >= 200 && env.status < 300) {
        setMsg({ ok: okText });
        await load();
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Action failed: ${e.message}` });
    }
    setBusyId('');
  };

  const fulfill = (r) => {
    const notes = typeof window !== 'undefined' ? window.prompt(`Fulfill request #${r.id} (${r.type}).\n\nOptional admin notes (leave blank to skip):`) : '';
    if (notes === null) return; // cancelled
    run(`r${r.id}`, M_PROCESS, { dsarRequestId: String(r.id), notes: notes.trim() ? notes.trim() : null }, 'processDsarRequest', `Request #${r.id} fulfilled.`);
  };

  const reject = (r) => {
    const reason = typeof window !== 'undefined' ? window.prompt(`Reject request #${r.id}? Reason (required, ≥ 3 chars):`) : '';
    if (!reason || reason.trim().length < 3) { if (reason !== null) setMsg({ error: 'A reason of at least 3 characters is required.' }); return; }
    run(`r${r.id}`, M_REJECT, { dsarRequestId: String(r.id), reason: reason.trim() }, 'rejectDsarRequest', `Request #${r.id} rejected.`);
  };

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Data Rights (DSAR)</h2>
      <div style={S.hint}>
        Consumer data-rights — pending data-subject access and deletion requests (GDPR/CCPA), oldest first. Fulfill the
        request (export/delete/correct) or reject it with a reason.
      </div>

      <div style={S.toolbar}>
        <button style={S.btnGhost} onClick={() => load()} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{requests.length} pending</span>
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}
      {loading && <div style={S.empty}>Loading…</div>}

      {!loading && (
        requests.length === 0 ? (
          <div style={S.empty}>No pending DSAR requests.</div>
        ) : requests.map((r) => (
          <div style={S.card} key={r.id}>
            <div style={S.rowTop}>
              <div>
                <p style={S.name}>
                  Request #{r.id}
                  {r.type ? <span style={S.typeTag}>{r.type}</span> : null}
                </p>
                <div style={S.meta}>
                  User {r.userId}
                  {r.requestedAt ? ` · Requested ${fmtDate(r.requestedAt)}` : ''}
                  {r.processedAt ? ` · Processed ${fmtDate(r.processedAt)}` : ''}
                  {r.processedBy ? ` by ${r.processedBy}` : ''}
                </div>
                {r.description ? <div style={S.meta}>{r.description}</div> : null}
                {r.rejectionReason ? <div style={S.meta}>Rejected: {r.rejectionReason}</div> : null}
                {r.resultUrl ? (
                  <div style={S.meta}><a style={S.link} href={r.resultUrl} target="_blank" rel="noreferrer">Download export →</a></div>
                ) : null}
              </div>
              <span style={S.badge(r.status)}>{r.status || '—'}</span>
            </div>
            {isPending(r) && (
              <div style={S.actions}>
                <button style={S.btn} disabled={busyId === `r${r.id}`} onClick={() => fulfill(r)}>
                  {busyId === `r${r.id}` ? '…' : 'Fulfill'}
                </button>
                <button style={S.btnRed} disabled={busyId === `r${r.id}`} onClick={() => reject(r)}>
                  Reject
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default Dsar;
